import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, onSnapshot, orderBy } from "firebase/firestore";
import { db } from "../firebase/firebaseConfig";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { toast } from "react-toastify";
import { RingLoader } from "react-spinners";
import RefundButton from "../components/Refundbutton";
import {
  FaReceipt,
  FaFilePdf,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle,
  FaWallet,
  FaCreditCard,
} from "react-icons/fa";

const OrderHistory = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = Math.floor(seconds % 60);
    return `${m}m ${s < 10 ? "0" : ""}${s}s`;
  };

  useEffect(() => {
    if (!currentUser || !currentUser.uid) {
      toast.error("Please login first to access your order history.");
      navigate("/login");
      return;
    }

    const userId = currentUser.uid;

    // Listen to orders from user's orders subcollection
    const orderRef = collection(db, "users", userId, "orders");
    const q = query(orderRef, orderBy("date", "desc"));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const orderList = snapshot.docs.map((doc) => {
          const data = doc.data();
          const orderTime = data.date?.toDate();
          return {
            id: doc.id,
            ...data,
            orderTime,
            isUrgent: data.isUrgent || false,
          };
        });
        setOrders(orderList);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching orders:", error);
        toast.error("Failed to fetch order history.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, navigate]);

  const getPaymentMethodIcon = (paymentId) => {
    return paymentId === "wallet-only" ? (
      <FaWallet className="text-green-500" title="Wallet Payment" />
    ) : (
      <FaCreditCard className="text-blue-500" title="Razorpay Payment" />
    );
  };

  const getPaymentMethodText = (paymentId) => {
    return paymentId === "wallet-only" ? "Wallet" : "Razorpay";
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Completed":
        return "text-green-500";
      case "Processing":
        return "text-yellow-500";
      case "Refunded":
        return "text-purple-500";
      case "Cancelled":
        return "text-red-500";
      default:
        return "text-gray-500";
    }
  };

  const renderedOrders = useMemo(() => {
    return orders.length > 0 ? (
      orders.map((order) => {
        const elapsedSeconds = order.orderTime ? (currentTime - order.orderTime) / 1000 : 0;
        const remainingSeconds = order.orderTime ? Math.max(180 - elapsedSeconds, 0) : 0;

        return (
          <div
            key={order.id}
            className={`p-4 rounded-lg transition-all duration-300 ${
              darkMode ? "bg-gray-700 hover:bg-gray-600" : "bg-gray-50 hover:bg-gray-100"
            }`}
          >
            <div
              className="flex justify-between items-center cursor-pointer"
              onClick={() => setExpandedOrderId(expandedOrderId === order.id ? null : order.id)}
            >
              <div>
                <p className="text-lg font-semibold">
                  📜 Order ID: <span className="text-indigo-500">{order.orderId}</span>
                </p>
                <p className="text-sm text-gray-400">
                  📅{" "}
                  {order.readableDate ||
                    (order.orderTime ? order.orderTime.toLocaleString() : "Unknown Date")}
                </p>
                {order.orderTime && !order.isUrgent && (
                  <p className="text-sm text-gray-400">
                    Refund Timer: {remainingSeconds > 0 ? formatTime(remainingSeconds) : "Expired"}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2">
                <p className={`text-sm font-semibold ${getStatusColor(order.status)}`}>
                  {order.status}
                </p>
                {getPaymentMethodIcon(order.paymentId)}
                {order.printed && <FaCheckCircle className="text-green-500" title="Printed" />}
                {expandedOrderId === order.id ? (
                  <FaChevronUp className="text-gray-400" />
                ) : (
                  <FaChevronDown className="text-gray-400" />
                )}
              </div>
            </div>

            {expandedOrderId === order.id && (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-400">💰 Total Amount:</p>
                    <p className="font-semibold">₹{order.totalAmount?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">💳 Payment Method:</p>
                    <p className="font-semibold">{getPaymentMethodText(order.paymentId)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">🚀 Urgent Order:</p>
                    <p className="font-semibold">{order.isUrgent ? "Yes" : "No"}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">📦 Items Count:</p>
                    <p className="font-semibold">{order.itemsCount}</p>
                  </div>
                  {order.paymentId && order.paymentId !== "wallet-only" && (
                    <div className="col-span-2">
                      <p className="text-gray-400">🔗 Payment ID:</p>
                      <p className="font-semibold text-xs break-all">{order.paymentId}</p>
                    </div>
                  )}
                </div>

                <div className="mt-3">
                  <p className="text-sm text-gray-400 mb-2">📁 Files:</p>
                  <ol className="space-y-2">
                    {order.items && Array.isArray(order.items) ? (
                      order.items.map((item, index) => (
                        <li
                          key={index}
                          className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300"
                        >
                          <span className="text-indigo-500">{index + 1}.</span>
                          <span className="truncate">{item.originalName}</span>
                          <span className="text-green-500 ml-2">₹{item.price?.toFixed(2)}</span>
                          <FaFilePdf className="text-red-500" />
                        </li>
                      ))
                    ) : (
                      <p className="text-gray-500 dark:text-gray-400">No files available</p>
                    )}
                  </ol>
                </div>

                <RefundButton order={order} />

                {order.printed && (
                  <div className="mt-3 p-3 bg-green-100 dark:bg-green-900 rounded-lg">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      🖨️ This order has been printed and is ready for pickup.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })
    ) : (
      <p className="text-center text-gray-500">No orders found</p>
    );
  }, [orders, expandedOrderId, darkMode, currentTime]);

  return (
    <div
      className={`min-h-screen flex flex-col items-center px-4 py-6 transition-all duration-300 ${
        darkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-gray-900"
      }`}
    >
      <h2 className="text-3xl font-semibold mb-6 flex items-center gap-3">
        <FaReceipt className="text-indigo-500 text-2xl" />
        Order History
      </h2>

      {loading ? (
        <div className="flex justify-center items-center h-96">
          <RingLoader color={darkMode ? "#ffffff" : "#4B5563"} loading={loading} size={50} />
        </div>
      ) : (
        <div
          className={`w-full max-w-4xl p-4 sm:p-6 shadow-lg rounded-xl ${
            darkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-300"
          } border`}
        >
          <div className="space-y-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
            {renderedOrders}
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
